<script setup>
import { ref, computed } from 'vue';
import { Eye, EyeOff } from 'lucide-vue-next';
import { cn } from '@/lib/utils';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: 'text',
    validator: (value) => ['text', 'email', 'tel', 'password'].includes(value),
  },
  label: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  required: {
    type: Boolean,
    default: false,
  },
  maxlength: {
    type: [String, Number],
    default: null,
  },
  autocomplete: {
    type: String,
    default: null,
  },
  name: {
    type: String,
    default: null,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  showPasswordToggle: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);

const inputRef = ref(null);
const showPassword = ref(false);

const inputValue = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
});

const inputType = computed(() => {
  if (props.type === 'password' && props.showPasswordToggle) {
    return showPassword.value ? 'text' : 'password';
  }
  return props.type;
});

const focus = () => {
  inputRef.value?.focus();
};

const scrollIntoView = (options) => {
  inputRef.value?.scrollIntoView(options);
};

defineExpose({
  focus,
  scrollIntoView,
});
</script>

<template>
  <div class="space-y-2">
    <div
      :class="
        cn(
          'relative flex items-center gap-2 h-10 border border-black/10 rounded-[12px] bg-white/70 backdrop-blur-sm transition-all duration-300 focus-within:ring-2 focus-within:ring-black/10 focus-within:border-black/20',
          disabled && 'opacity-50 cursor-not-allowed'
        )
      "
    >
      <span
        v-if="label"
        class="pl-3 text-sm font-normal text-black/60 whitespace-nowrap flex-shrink-0"
      >
        {{ label }}
      </span>
      <input
        ref="inputRef"
        v-model="inputValue"
        :type="inputType"
        :required="required"
        :maxlength="maxlength"
        :autocomplete="autocomplete"
        :name="name"
        :disabled="disabled"
        :class="
          cn(
            'flex-1 h-full bg-transparent text-sm text-black placeholder-black/20 focus:outline-none font-light',
            !label && 'pl-3',
            showPasswordToggle && type === 'password' ? 'pr-11' : 'pr-4'
          )
        "
        :placeholder="placeholder"
      />
      <button
        v-if="showPasswordToggle && type === 'password'"
        type="button"
        class="absolute right-4 top-1/2 -translate-y-1/2 text-black/40 hover:text-black/60 transition-colors"
        @click="showPassword = !showPassword"
      >
        <Eye v-if="!showPassword" class="h-4 w-4" />
        <EyeOff v-else class="h-4 w-4" />
      </button>
    </div>
  </div>
</template>
